import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '@/lib/supabase';
import { clearAuthHeadersCache, primeAuthHeaders } from '@/lib/orchestration-api';
import { getCurrentSession } from '@/features/auth/api/login';

export type UserRole = 'customer' | 'admin' | 'super-admin' | 'rider';

export interface AuthUser {
  id: string;
  email: string;
  username: string;
  name: string;
  role: UserRole;
  phone?: string;
  branchId?: string;
  avatar?: string;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  previousRole: UserRole | null;
  
  setUser: (user: AuthUser | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  login: (user: AuthUser) => void;
  logout: () => void;
  clearError: () => void;
  switchRole: (role: UserRole) => void;
  clearSession: () => void;
  hasRole: (role: UserRole | UserRole[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      previousRole: null,

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      login: (user) => set({ user, isAuthenticated: true, isLoading: false, error: null }),
      logout: () => set({ user: null, isAuthenticated: false, isLoading: false, error: null, previousRole: null }),
      clearError: () => set({ error: null }),
      
      switchRole: (role) => {
        const { user } = get();
        if (user) {
          const previousRole = user.role;
          set({ user: { ...user, role }, previousRole, isLoading: false, error: null });
        }
      },
      
      clearSession: () => {
        set({ user: null, isAuthenticated: false, isLoading: false, error: null, previousRole: null });
        sessionStorage.removeItem('karebe-auth');
        clearAuthHeadersCache();
        // Also sign out from Supabase if possible
        if (supabase) {
          supabase.auth.signOut().catch(() => {});
        }
      },
      
      hasRole: (role) => {
        const { user } = get();
        if (!user) return false;
        
        const roleHierarchy: Record<string, number> = {
          'super-admin': 3,
          'admin': 2,
          'rider': 1,
          'customer': 0,
        };
        
        const userRoleLevel = roleHierarchy[user.role] ?? 0;
        
        if (Array.isArray(role)) {
          return role.some(requiredRole => {
            const requiredLevel = roleHierarchy[requiredRole] ?? 0;
            return userRoleLevel >= requiredLevel;
          });
        }
        
        const requiredLevel = roleHierarchy[role] ?? 0;
        return userRoleLevel >= requiredLevel;
      },
    }),
    {
      name: 'karebe-auth',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ 
        user: state.user, 
        isAuthenticated: state.isAuthenticated,
        previousRole: state.previousRole,
      }),
    }
  )
);

export const useAuthUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);
export const useHasRole = (role: UserRole | UserRole[]) => useAuthStore((state) => state.hasRole(role));
export const usePreviousRole = () => useAuthStore((state) => state.previousRole);
export const useSwitchRole = () => useAuthStore((state) => state.switchRole);
export const useClearSession = () => useAuthStore((state) => state.clearSession);

// Initialize auth state from Supabase session on app load
export async function initializeAuth() {
  try {
    const session = await getCurrentSession();
    if (session?.user) {
      primeAuthHeaders(session.access_token);
      let adminProfile: Record<string, any> | null = null;

      if (session.user.email) {
        const emailResult = await supabase!
          .from('admin_users')
          .select('*')
          .eq('email', session.user.email)
          .maybeSingle();

        if (!emailResult.error) {
          adminProfile = emailResult.data;
        }
      }

      if (!adminProfile) {
        const authUserIdResult = await supabase!
          .from('admin_users')
          .select('*')
          .eq('auth_user_id', session.user.id)
          .maybeSingle();

        if (!authUserIdResult.error) {
          adminProfile = authUserIdResult.data;
        }
      }

      const role = (adminProfile?.role || 'admin').replace('_', '-') as UserRole;
      
      useAuthStore.getState().login({
        id: session.user.id,
        email: session.user.email || '',
        username: session.user.email || '',
        name: adminProfile?.name || session.user.user_metadata?.name || '',
        role,
        phone: adminProfile?.phone,
        branchId: adminProfile?.branch_id,
        avatar: session.user.user_metadata?.avatar,
      });
    }
  } catch (error) {
    console.error('[Auth] Failed to initialize from session:', error);
  }

  // Listen for auth changes
  supabase?.auth.onAuthStateChange((_event, session) => {
    if (session?.user) {
      primeAuthHeaders(session.access_token);
      // Session created/updated
    } else {
      clearAuthHeadersCache();
      primeAuthHeaders(null);
      // Session removed
      useAuthStore.getState().logout();
    }
  });
}
